/**
 * LegalPages.jsx — Privacy Policy & Terms of Use for PriceBuddy
 * by IponBuddy
 *
 * Usage: drop into your router as:
 *   /privacy  → <LegalPage page="privacy" />
 *   /terms    → <LegalPage page="terms" />
 *
 * Or render the combined switcher:
 *   <LegalPages />
 */

import React, { useState, useEffect } from 'react';

// ─── Palette (mirrors App.jsx) ─────────────────────────────────────────────────
const T = {
  bg:    '#F9F6F0', bg1:   '#F3EFE6', bg2:  '#EBE6DA', white: '#FFFDF9',
  ink:   '#18160F', sub:   '#6B6459', dim:  '#A89F93',
  line:  'rgba(24,22,15,0.08)', line2: 'rgba(24,22,15,0.14)',
  g:     '#185C33', gL:    '#E8F2EC', gM:   '#3A9E62', gPop: '#1F7D44',
  a:     '#A86200', aL:    '#FDF3DC', aBdr: '#E8C97A',
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,300;1,9..144,500&family=Instrument+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
  :root{--serif:'Fraunces',Georgia,serif;--sans:'Instrument Sans',system-ui,sans-serif;--mono:'DM Mono','Courier New',monospace;--ease:cubic-bezier(.25,.46,.45,.94);}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  html{font-size:16px;-webkit-font-smoothing:antialiased;}
  body{background:${T.bg};color:${T.ink};font-family:var(--sans);line-height:1.6;min-height:100vh;}
  a{color:${T.g};text-decoration:none;}
  a:hover{text-decoration:underline;}
  button{font-family:var(--sans);cursor:pointer;border:none;background:none;}
  ::selection{background:${T.gL};color:${T.g};}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-track{background:transparent;}
  ::-webkit-scrollbar-thumb{background:${T.bg2};border-radius:4px;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
  .legal-section{animation:fadeUp .35s var(--ease) both;}
  .legal-section + .legal-section{margin-top:40px;}
  .legal-h2{font-family:var(--serif);font-size:19px;font-weight:500;color:${T.ink};margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid ${T.line};}
  .legal-p{font-size:14px;color:${T.sub};line-height:1.8;margin-bottom:10px;}
  .legal-p:last-child{margin-bottom:0;}
  .legal-ul{list-style:none;padding:0;margin:10px 0;}
  .legal-ul li{font-size:14px;color:${T.sub};line-height:1.8;padding:4px 0 4px 18px;position:relative;}
  .legal-ul li::before{content:'—';position:absolute;left:0;color:${T.dim};font-family:var(--mono);font-size:11px;}
  .legal-inline-badge{display:inline-flex;align-items:center;gap:5px;background:${T.aL};border:1px solid ${T.aBdr};border-radius:5px;padding:2px 9px;font-family:var(--mono);font-size:10px;color:${T.a};font-weight:500;letter-spacing:0.3px;vertical-align:middle;margin-left:6px;}
  .legal-highlight{background:${T.gL};border-left:3px solid ${T.gM};border-radius:0 8px 8px 0;padding:14px 18px;margin:16px 0;font-size:14px;color:${T.sub};line-height:1.8;}
  .legal-toc{background:${T.white};border:1px solid ${T.line};border-radius:12px;padding:20px 22px;margin-bottom:36px;}
  .legal-toc-title{font-family:var(--mono);font-size:10px;font-weight:500;letter-spacing:0.8px;color:${T.dim};margin-bottom:14px;}
  .legal-toc a{font-size:13px;color:${T.sub};display:block;padding:4px 0;border-bottom:1px solid ${T.line};transition:color .15s;}
  .legal-toc a:last-child{border-bottom:none;}
  .legal-toc a:hover{color:${T.g};text-decoration:none;}
  .legal-toc a span{float:right;font-family:var(--mono);font-size:10px;color:${T.dim};}
`;

// ─── PRIVACY POLICY content ────────────────────────────────────────────────────
const PRIVACY_SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    tag: 'plain English',
    content: (
      <>
        <p className="legal-p">PriceBuddy is a community-sourced grocery price tracker powered by receipt scans from the IponBuddy app. This Privacy Policy explains what data we collect, why we collect it, and how we protect it.</p>
        <div className="legal-highlight">
          We do not sell your personal data. We do not run ads. We do not share your information with third parties for marketing purposes. This service is free and community-powered.
        </div>
      </>
    ),
  },
  {
    id: 'data-we-collect',
    title: 'Data we collect',
    tag: null,
    content: (
      <>
        <p className="legal-p">When you use PriceBuddy (this website), we collect only:</p>
        <ul className="legal-ul">
          <li>Anonymous analytics: page views, search queries, and interactions (no personal identifiers attached).</li>
          <li>Country and location filters you select — stored locally in your browser session only.</li>
          <li>Technical data automatically sent by your browser: IP address (truncated), user agent, and referrer.</li>
        </ul>
        <p className="legal-p" style={{ marginTop: 14 }}>When you scan a receipt on the IponBuddy mobile app and choose to share prices with the community:</p>
        <ul className="legal-ul">
          <li>Product name, price, store name, and city/area are extracted via OCR and stored.</li>
          <li>No name, email, phone number, or account identifier is attached to contributed price data.</li>
          <li>Sharing is <strong>opt-in</strong> — you explicitly toggle this in the app before your receipt is processed.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-we-use-data',
    title: 'How we use your data',
    tag: null,
    content: (
      <>
        <ul className="legal-ul">
          <li>To power the public price map — showing aggregated, anonymized price averages by product, store, and location.</li>
          <li>To detect price movements and surface price change alerts to users watching a product.</li>
          <li>To improve the accuracy of our OCR models and product name normalization.</li>
          <li>To generate platform-wide statistics (total products tracked, total reports, cities, stores).</li>
        </ul>
        <p className="legal-p" style={{ marginTop: 12 }}>We never use your data to build a personal profile, serve targeted ads, or enrich any third-party database.</p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies & local storage',
    tag: null,
    content: (
      <>
        <p className="legal-p">PriceBuddy uses minimal browser storage:</p>
        <ul className="legal-ul">
          <li><strong>Session storage:</strong> your selected country and location filter — cleared when you close the tab.</li>
          <li><strong>Analytics cookies:</strong> anonymous, first-party only. No cross-site tracking cookies.</li>
          <li><strong>No advertising cookies.</strong> No Facebook Pixel. No Google Ads.</li>
        </ul>
        <p className="legal-p" style={{ marginTop: 12 }}>You can clear all cookies and local storage at any time through your browser settings without losing any account data (there is no account).</p>
      </>
    ),
  },
  {
    id: 'third-parties',
    title: 'Third parties',
    tag: null,
    content: (
      <>
        <p className="legal-p">We use a small number of third-party services to operate the platform:</p>
        <ul className="legal-ul">
          <li><strong>Google Fonts</strong> — for typography. Subject to Google's privacy policy.</li>
          <li><strong>Google Play</strong> — for the IponBuddy app download link. No data is shared with Google when you click the link.</li>
          {/* <li><strong>Our API server</strong> (api-iponbuddy.vincedaniel.com) — receives your search queries and location filter to return price results. No personal data is logged beyond standard server access logs (truncated IP, timestamp, query).</li> */}
          <li><strong>Our API server</strong> — receives your search queries and location filter to return price results. No personal data is logged beyond standard server access logs (truncated IP, timestamp, query).</li>
        </ul>
        <p className="legal-p" style={{ marginTop: 12 }}>We do not integrate with Facebook, Twitter/X, TikTok, or any advertising network.</p>
      </>
    ),
  },
  {
    id: 'data-retention',
    title: 'Data retention & deletion',
    tag: null,
    content: (
      <>
        <p className="legal-p">Aggregated price data contributed via IponBuddy is retained indefinitely to power price trend history. However, because price data is anonymized at the point of collection, there is no personal record to delete.</p>
        <p className="legal-p">If you believe your data has been collected incorrectly, contact us at <a href="mailto:vincedaniel20@gmail.com">vincedaniel20@gmail.com</a> and we will investigate within 14 days.</p>
      </>
    ),
  },
  {
    id: 'children',
    title: "Children's privacy",
    tag: null,
    content: (
      <p className="legal-p">PriceBuddy and IponBuddy are not directed at children under 13. We do not knowingly collect data from children. If you believe a child has submitted data, contact us immediately.</p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    tag: null,
    content: (
      <p className="legal-p">We may update this Privacy Policy from time to time. Significant changes will be noted at the top of this page with a revised effective date. Continued use of PriceBuddy after changes constitutes acceptance.</p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    tag: null,
    content: (
      <p className="legal-p">Questions? Reach us at <a href="mailto:vincedaniel20@gmail.com">vincedaniel20@gmail.com</a>. For IponBuddy app issues, use the in-app feedback form.</p>
    ),
  },
];

// ─── TERMS OF USE content ──────────────────────────────────────────────────────
const TERMS_SECTIONS = [
  {
    id: 'acceptance',
    title: 'Acceptance of terms',
    tag: null,
    content: (
      <p className="legal-p">By accessing or using PriceBuddy ("the Service"), you agree to be bound by these Terms of Use. If you disagree, please stop using the Service. These terms apply to the PriceBuddy website and, where noted, to the IponBuddy mobile app.</p>
    ),
  },
  {
    id: 'description',
    title: 'Description of service',
    tag: null,
    content: (
      <>
        <p className="legal-p">PriceBuddy is a free, community-sourced grocery price tracker. Prices displayed are contributed by users of the IponBuddy app through voluntary opt-in receipt scanning. We aggregate and display this data to help consumers compare grocery prices.</p>
        <div className="legal-highlight">
          Prices shown are community-reported averages and are not guaranteed to reflect current shelf prices at any store. Always verify prices in person before making purchasing decisions.
        </div>
      </>
    ),
  },
  {
    id: 'accuracy',
    title: 'Price accuracy disclaimer',
    tag: 'important',
    content: (
      <>
        <p className="legal-p">The price data on PriceBuddy is:</p>
        <ul className="legal-ul">
          <li>Contributed by community members and may be outdated by hours, days, or weeks.</li>
          <li>Subject to OCR extraction errors from receipt scans.</li>
          <li>Not verified against any official store database or pricing system.</li>
          <li>Subject to regional variation — a price in one city may not apply to another.</li>
        </ul>
        <p className="legal-p" style={{ marginTop: 12 }}>IponBuddy and PriceBuddy make no warranties, expressed or implied, about the accuracy, completeness, or timeliness of price data. We are not liable for any purchasing decisions made based on prices shown on this platform.</p>
      </>
    ),
  },
  {
    id: 'user-contributions',
    title: 'User contributions',
    tag: null,
    content: (
      <>
        <p className="legal-p">When you contribute price data through the IponBuddy app, you:</p>
        <ul className="legal-ul">
          <li>Grant IponBuddy a worldwide, royalty-free, perpetual license to display, aggregate, and use the submitted price data in anonymized form.</li>
          <li>Confirm that the receipt you scanned represents a genuine, real-world purchase.</li>
          <li>Agree not to submit fabricated, manipulated, or intentionally misleading price data.</li>
        </ul>
        <p className="legal-p" style={{ marginTop: 12 }}>We reserve the right to remove any price data that we reasonably believe is inaccurate, fraudulent, or submitted in bad faith.</p>
      </>
    ),
  },
  {
    id: 'prohibited',
    title: 'Prohibited use',
    tag: null,
    content: (
      <>
        <p className="legal-p">You may not use PriceBuddy to:</p>
        <ul className="legal-ul">
          <li>Scrape or systematically download price data for commercial purposes without written permission.</li>
          <li>Reverse-engineer, decompile, or attempt to extract the underlying dataset.</li>
          <li>Submit false or manipulated price data to distort the community map.</li>
          <li>Attempt to identify individual contributors from anonymized price records.</li>
          <li>Use the platform in any way that violates applicable law in your jurisdiction.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'ip',
    title: 'Intellectual property',
    tag: null,
    content: (
      <p className="legal-p">The PriceBuddy and IponBuddy names, logos, UI design, and codebase are owned by IponBuddy and protected by copyright and trademark law. The aggregated, anonymized price data displayed on the platform may be freely viewed and shared for personal, non-commercial use with attribution.</p>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    tag: null,
    content: (
      <p className="legal-p">To the maximum extent permitted by law, IponBuddy shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of PriceBuddy — including, without limitation, any loss resulting from reliance on displayed prices. Our total liability for any claim shall not exceed PHP 500.</p>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing law',
    tag: null,
    content: (
      <p className="legal-p">These Terms are governed by the laws of the Republic of the Philippines. Any disputes shall be submitted to the courts of Metro Manila, Philippines, unless otherwise required by applicable consumer protection law in your jurisdiction.</p>
    ),
  },
  {
    id: 'changes-terms',
    title: 'Changes to these terms',
    tag: null,
    content: (
      <p className="legal-p">We may update these Terms at any time. Continued use after changes are posted constitutes acceptance of the revised terms. Major changes will be announced via the IponBuddy app.</p>
    ),
  },
  {
    id: 'contact-terms',
    title: 'Contact',
    tag: null,
    content: (
      <p className="legal-p">Questions about these Terms? Email <a href="mailto:vincedaniel20@gmail.com">vincedaniel20@gmail.com</a>.</p>
    ),
  },
];

// ─── TOC ───────────────────────────────────────────────────────────────────────
function TableOfContents({ sections }) {
  return (
    <div className="legal-toc">
      <div className="legal-toc-title">TABLE OF CONTENTS</div>
      {sections.map((s, i) => (
        <a key={s.id} href={`#${s.id}`}>
          {s.title}
          <span>{String(i + 1).padStart(2, '0')}</span>
        </a>
      ))}
    </div>
  );
}

// ─── Single section ────────────────────────────────────────────────────────────
function LegalSection({ section, index }) {
  return (
    <div
      id={section.id}
      className="legal-section"
      style={{ animationDelay: `${index * 35}ms` }}
    >
      <h2 className="legal-h2">
        {section.title}
        {section.tag && (
          <span className="legal-inline-badge">{section.tag.toUpperCase()}</span>
        )}
      </h2>
      {section.content}
    </div>
  );
}

// ─── Nav ───────────────────────────────────────────────────────────────────────
function LegalNav({ active, onChange }) {
  return (
    <nav style={{
      borderBottom: `1px solid ${T.line}`,
      background: T.bg,
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <div style={{
        maxWidth: 760, margin: '0 auto', padding: '0 24px',
        height: 56, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 16,
      }}>
        {/* Back link */}
        <a href="/" style={{
          display: 'flex', alignItems: 'center', gap: 7,
          fontSize: 13, color: T.sub, textDecoration: 'none',
          flexShrink: 0,
        }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, color: T.dim }}>←</span>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 500, color: T.ink, letterSpacing: -0.2 }}>PriceBuddy</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: T.dim }}>by IponBuddy</span>
        </a>

        {/* Tab switcher */}
        <div style={{
          display: 'flex', background: T.bg1,
          border: `1px solid ${T.line2}`, borderRadius: 8, padding: 3, gap: 2,
        }}>
          {[
            { key: 'privacy', label: 'Privacy Policy' },
            { key: 'terms',   label: 'Terms of Use' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => onChange(t.key)}
              style={{
                padding: '6px 14px', borderRadius: 6,
                fontSize: 12.5, fontWeight: active === t.key ? 600 : 400,
                background: active === t.key ? T.white : 'transparent',
                color: active === t.key ? T.ink : T.sub,
                border: active === t.key ? `1px solid ${T.line2}` : '1px solid transparent',
                transition: 'all .15s',
                boxShadow: active === t.key ? '0 1px 3px rgba(24,22,15,0.07)' : 'none',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function LegalPages({ initialPage = 'privacy' }) {
  const [page, setPage] = useState(initialPage);

  const sections = page === 'privacy' ? PRIVACY_SECTIONS : TERMS_SECTIONS;

  const effectiveDate = page === 'privacy' ? 'April 17, 2026' : 'April 17, 2026';

  useEffect(() => {
    const titles = {
      privacy: 'Privacy Policy · PriceBuddy by IponBuddy',
      terms:   'Terms of Use · PriceBuddy by IponBuddy',
    };
    document.title = titles[page];
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <LegalNav active={page} onChange={setPage} />

        <main style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px', flex: 1, width: '100%' }}>

          {/* Header */}
          <div style={{ marginBottom: 40, paddingBottom: 32, borderBottom: `1px solid ${T.line}` }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: T.gL, border: `1px solid #9DD4B2`,
              borderRadius: 6, padding: '4px 12px', marginBottom: 20,
            }}>
              <span style={{ fontSize: 12 }}>{page === 'privacy' ? '🔒' : '📄'}</span>
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 10.5, color: T.g,
                fontWeight: 500, letterSpacing: 0.4,
              }}>
                {page === 'privacy' ? 'PRIVACY POLICY' : 'TERMS OF USE'}
              </span>
            </div>

            <h1 style={{
              fontFamily: 'var(--serif)',
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: 300, color: T.ink,
              lineHeight: 1.15, letterSpacing: -1,
              marginBottom: 14,
            }}>
              {page === 'privacy' ? (
                <>How we handle<br /><em style={{ color: T.g, fontStyle: 'italic', fontWeight: 500 }}>your data.</em></>
              ) : (
                <>Fair rules for a<br /><em style={{ color: T.g, fontStyle: 'italic', fontWeight: 500 }}>free community.</em></>
              )}
            </h1>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: T.sub }}>
                Effective: <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{effectiveDate}</span>
              </span>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
              <span style={{ fontSize: 13, color: T.sub }}>
                Jurisdiction: <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>Republic of the Philippines</span>
              </span>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 500,
                color: T.g, background: T.gL, border: `1px solid #9DD4B2`,
                borderRadius: 4, padding: '2px 8px',
              }}>
                {page === 'privacy' ? 'NO ADS · NO DATA SOLD' : 'FREE FOREVER'}
              </span>
            </div>
          </div>

          {/* Table of Contents */}
          <TableOfContents sections={sections} />

          {/* Sections */}
          {sections.map((s, i) => (
            <LegalSection key={s.id} section={s} index={i} />
          ))}

          {/* Bottom CTA */}
          <div style={{
            marginTop: 56, background: T.bg1,
            border: `1px solid ${T.line2}`, borderRadius: 14,
            padding: '28px 28px', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between',
            gap: 20, flexWrap: 'wrap',
          }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
                Still have questions?
              </p>
              <p style={{ fontSize: 13, color: T.sub }}>
                We're a small team — email us and we'll reply within 2 business days.
              </p>
            </div>
            <a
              href={`mailto:${page === 'privacy' ? 'privacy' : 'legal'}@iponbuddy.vincedaniel.com`}
              style={{
                background: T.g, color: '#fff', borderRadius: 9,
                padding: '11px 20px', fontSize: 13, fontWeight: 600,
                display: 'inline-block', flexShrink: 0,
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              Contact us ↗
            </a>
          </div>
        </main>

        {/* Footer */}
        <footer style={{
          borderTop: `1px solid ${T.line}`,
          padding: '20px 24px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexWrap: 'wrap', gap: '8px 16px',
        }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: T.dim }}>© 2026 IponBuddy</span>
          <span style={{ color: T.line2, fontSize: 10 }}>·</span>
          <a href="/" style={{ fontSize: 11, color: T.sub }}>Home</a>
          <span style={{ color: T.line2, fontSize: 10 }}>·</span>
          <button
            onClick={() => setPage('privacy')}
            style={{ fontSize: 11, color: page === 'privacy' ? T.g : T.sub, fontWeight: page === 'privacy' ? 600 : 400 }}
          >
            Privacy
          </button>
          <span style={{ color: T.line2, fontSize: 10 }}>·</span>
          <button
            onClick={() => setPage('terms')}
            style={{ fontSize: 11, color: page === 'terms' ? T.g : T.sub, fontWeight: page === 'terms' ? 600 : 400 }}
          >
            Terms
          </button>
        </footer>
      </div>
    </>
  );
}

// ─── Individual page exports (for router usage) ────────────────────────────────
export function PrivacyPage() {
  return <LegalPages initialPage="privacy" />;
}

export function TermsPage() {
  return <LegalPages initialPage="terms" />;
}